import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { http } from "../../../../services/HttpService";
import { HTTP_ENDPOINTS, QUERY_KEYS } from "../../../../consts/api";
import { useInvoice } from "./invoice/useInvoice";
import { uiNotification } from "../../../../services/UINotificationService";
import { checkoutInvoice } from "../../../../store/checkoutInvoice";
import { dispatch } from "../../../../store/store";

const useChargePfi = (options = {}) => {
  const res = useMutation(
    (dto) => http.post(HTTP_ENDPOINTS.patientFiIntegration.chargePfi(), dto),
    {
      onError: (e) => uiNotification.error(e.response.data.message),
      ...options,
    },
  );
  return res;
};

const useCheckPfiStatus = (onGoingPayment, options = {}) => {
  const { invoice } = useInvoice();
  const { id } = invoice;
  const res = useQuery(
    [QUERY_KEYS.patientFi.checkPfiStatus, onGoingPayment],
    () =>
      http.get(HTTP_ENDPOINTS.patientFiIntegration.checkPfiPaymentStatus(id)),
    {
      enabled: Boolean(onGoingPayment),
      refetchInterval: (data) => {
        return !data?.data?.data?.processing ? 3000 : false;
      },
      refetchOnWindowFocus: false,
      retry: false,
      ...options,
    },
  );
  return res;
};

const useCancelPfiTransaction = (options = {}) => {
  const { invoice } = useInvoice();
  const { id } = invoice;

  const res = useMutation(
    (dto) =>
      http.delete(
        HTTP_ENDPOINTS.patientFiIntegration.cancelPfiTransaction(id),
        dto,
      ),
    {
      onError: (e) => uiNotification.error(e.response.data.message),
      ...options,
    },
  );
  return res;
};

const useResendNotification = (options = {}) => {
  const { invoice } = useInvoice();
  const { id } = invoice;

  const res = useMutation(
    (dto) =>
      http.post(
        HTTP_ENDPOINTS.patientFiIntegration.resendNotification(id),
        dto,
      ),
    {
      onError: (e) => uiNotification.error(e.response.data.message),
      ...options,
    },
  );
  return res;
};

export const usePatientFi = () => {
  const [onGoingPayment, setOnGoingPayment] = useState(false);
  const { refetchInvoice } = useInvoice();

  const { mutate: chargePfi, isLoading: isLoadingChargePfi } = useChargePfi({
    onSuccess: () => setOnGoingPayment(true),
  });

  const checkStatus = async (res) => {
    const pfiPayment = res.pfiPayment;
    if (pfiPayment.cancelled) {
      setOnGoingPayment(false);
    }
    if (!pfiPayment.processing && pfiPayment.fulfilled) {
      setOnGoingPayment(false);
      await refetchInvoice();
      dispatch(checkoutInvoice.actions.paymentViewClose());
    }
  };

  const { isLoading: isCheckingStatus } = useCheckPfiStatus(onGoingPayment, {
    select: (res) => res.data.data,
    onSuccess: (res) => checkStatus(res),
  });

  const {
    mutate: cancelPfiTransaction,
    isLoading: isLoadingCancelPfiTransaction,
  } = useCancelPfiTransaction();

  const { mutate: resendNotification, isLoading: isResending } =
    useResendNotification();

  const isLoading = isLoadingChargePfi || isLoadingCancelPfiTransaction;

  return {
    chargePfi,
    isLoading,
    isCheckingStatus,
    cancelPfiTransaction,
    onGoingPayment: {
      value: onGoingPayment,
      set: setOnGoingPayment,
    },
    resendNotification: {
      mutate: resendNotification,
      isLoading: isResending,
    },
  };
};
